import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
  Headline,
  Tooltip,
} from '@lightricks/react-design-system';
import type {CallToAction} from '@contentlab/api/avatars/types';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewTransitionEvent from '@contentlab/hooks/analytics/use-view-transition-event';
import {PreGenerateForm} from '@contentlab/stores/contentLabStore';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {CUSTOM_GOAL_VALUE, GOAL_OPTIONS} from '../../constants/goalOptions';
import ProjectBasicsForm from '../shared/ProjectBasicsForm';

const MAX_PROMOTING_LENGTH = 50;

// Styled Components
const Container = styled(Box)(() => ({
  boxSizing: 'border-box',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  borderRadius: '32px',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
}));

const InnerContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '24px',
  flex: '1 0 0',
  alignSelf: 'stretch',
}));

const MagicPenIconContainer = styled(Box)(() => ({
  top: '16px',
  left: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  backgroundColor: designSystemToken('semantic.bg.neutral-secondary'),
  borderRadius: '50%',
}));

const HeaderSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const ActionArea = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '8px',
}));

interface PreGenerateScriptProps {
  testID?: string;
  assetId: string;
  formState: PreGenerateForm;
  onFormChange: (updates: Partial<PreGenerateForm>) => void;
  onScriptsGenerated?: (params: {
    assetId: string;
    promoting: string;
    goal: CallToAction | typeof CUSTOM_GOAL_VALUE;
    customGoal?: string;
  }) => void;
}

function PreGenerateScript({
  testID = 'pre-generate-script',
  assetId,
  formState,
  onFormChange,
  onScriptsGenerated,
}: PreGenerateScriptProps) {
  const {promoting, selectedGoal, customGoal} = formState;
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCustomGoalPopperOpen, setIsCustomGoalPopperOpen] = useState(false);
  const [customGoalAnchorEl, setCustomGoalAnchorEl] =
    useState<HTMLElement | null>(null);

  const {uiInteraction} = useUiInteractionEvent();
  const viewTransition = useViewTransitionEvent();

  useEffect(() => {
    const presentationId = uuid();
    const startTime = Date.now();
    viewTransition.presented({
      presentation_id: presentationId,
      view_name: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
      view_type: 'view',
      view_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
      source: 'Asset selected',
    });

    return () => {
      const endTime = Date.now();
      viewTransition.dismissed({
        interaction_id: uuid(),
        presentation_id: presentationId,
        reason: 'Generate Script initiated or user navigated away',
        view_details: `Total presented time: ${endTime - startTime} ms`,
      });
    };
  }, []);

  const handleGoalSelect = useCallback(
    (goal: CallToAction | typeof CUSTOM_GOAL_VALUE) => {
      onFormChange({selectedGoal: goal});
      uiInteraction({
        item_name: 'goal_chip_selection',
        interaction_location:
          CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
        interaction_details: goal,
        asset_id: assetId || '',
      });
    },
    [assetId, onFormChange]
  );

  const handleCustomGoalAcceptInternal = useCallback(
    (customGoalText: string) => {
      onFormChange({
        customGoal: customGoalText,
        selectedGoal: CUSTOM_GOAL_VALUE,
      });
      setIsCustomGoalPopperOpen(false);
      setCustomGoalAnchorEl(null);

      uiInteraction({
        item_name: 'custom_goal_chip_selection',
        interaction_location:
          CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
        interaction_details: customGoalText,
        asset_id: assetId || '',
      });
    },
    [assetId, onFormChange]
  );

  const handleCustomGoalPopperClose = useCallback(() => {
    setIsCustomGoalPopperOpen(false);
    setCustomGoalAnchorEl(null);
  }, []);

  const handleCustomGoalPopperOpen = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      setCustomGoalAnchorEl(event?.currentTarget || null);
      setIsCustomGoalPopperOpen(true);
    },
    []
  );

  const handleCustomGoalDeleteInternal = useCallback(() => {
    uiInteraction({
      item_name: 'custom_goal_delete_button',
      interaction_location:
        CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
      interaction_details: customGoal,
      asset_id: assetId || '',
    });

    onFormChange({customGoal: '', selectedGoal: null});
    // Clear the popper input when deleting
    setIsCustomGoalPopperOpen(false);
    setCustomGoalAnchorEl(null);
  }, [customGoal, assetId, onFormChange]);

  const handleGenerateScripts = useCallback(async () => {
    if (!assetId || isGenerating || !selectedGoal) return;

    uiInteraction({
      item_name: 'generate_script_button',
      interaction_location:
        CONTENT_LAB_VIEW_NAMES.AI_AVATAR_PRE_GENERATE_SCRIPT,
      interaction_details: JSON.stringify({
        promoting,
        goal: selectedGoal === CUSTOM_GOAL_VALUE ? customGoal : selectedGoal,
      }),
      asset_id: assetId || '',
    });
    try {
      setIsGenerating(true);
      await onScriptsGenerated?.({
        assetId,
        promoting: promoting.trim(),
        goal: selectedGoal,
        customGoal: selectedGoal === CUSTOM_GOAL_VALUE ? customGoal : undefined,
      });
      // Only stop loading if generation succeeds
      setIsGenerating(false);
    } catch (e) {
      // Error is already handled in parent (AiAvatarPage)
      // Keep loading state false so user can try again
      setIsGenerating(false);
    }
  }, [
    assetId,
    isGenerating,
    selectedGoal,
    promoting,
    customGoal,
    onScriptsGenerated,
  ]);

  const canGenerate = Boolean(
    assetId && selectedGoal && promoting.trim().length > 0 && !isGenerating
  );

  const areFieldsComplete = Boolean(
    selectedGoal && promoting.trim().length > 0
  );

  const getTooltipMessage = () => {
    if (areFieldsComplete && !assetId) {
      return translate('Please wait while your video finishes uploading');
    }
    return translate('Complete all required fields');
  };

  return (
    <Container data-testid={testID}>
      <InnerContainer>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <MagicPenIconContainer>
            <Icon
              name="Features-MagicPen"
              size="small"
              appearance="neutral"
              color={designSystemToken('semantic.fg.secondary')}
            />
          </MagicPenIconContainer>

          <HeaderSection>
            <Headline
              size="sm"
              color={designSystemToken('semantic.fg.primary')}
            >
              {translate('Generate your script')}
            </Headline>

            <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
              {translate(
                'Describe what you’re promoting and your goal — we’ll create a script to match.'
              )}
            </Body>
          </HeaderSection>
        </Box>

        <ProjectBasicsForm
          // Text input props
          showTextInput
          promoting={promoting}
          onPromotingChange={(value) => onFormChange({promoting: value})}
          promotingLabel={translate('What are you promoting?')}
          promotingPlaceholder={translate(
            'New feature launch, Spring sale, Learning app'
          )}
          maxCharacters={MAX_PROMOTING_LENGTH}
          showCharacterCounter
          // Chip selection props
          chipOptions={GOAL_OPTIONS}
          selectedValues={selectedGoal ? [selectedGoal] : []}
          onValueSelect={(value: string) =>
            handleGoalSelect(value as CallToAction | typeof CUSTOM_GOAL_VALUE)
          }
          chipSectionLabel={translate(
            "What's the primary goal for this avatar video?"
          )}
          // Custom option props
          showCustomOption
          customId={CUSTOM_GOAL_VALUE}
          customValue={customGoal}
          onCustomValueAccept={handleCustomGoalAcceptInternal}
          onCustomValueDelete={handleCustomGoalDeleteInternal}
          onCustomPopperOpen={handleCustomGoalPopperOpen}
          isCustomPopperOpen={isCustomGoalPopperOpen}
          onCustomPopperClose={handleCustomGoalPopperClose}
          customPopperAnchorEl={customGoalAnchorEl}
        />
      </InnerContainer>

      <ActionArea>
        <Tooltip
          title={
            <Body
              size="md"
              sx={{
                maxWidth: '185px',
                letterSpacing: '-0.2px',
                textAlign: 'center',
              }}
            >
              {getTooltipMessage()}
            </Body>
          }
          placement="top"
          arrow
          disableHoverListener={canGenerate}
        >
          <Box>
            <Button
              appearance="neutral"
              size="large"
              mode="filled"
              onClick={handleGenerateScripts}
              disabled={!canGenerate}
              isLoading={isGenerating}
              sx={{
                width: 'auto',
                minWidth: '160px',
                height: '48px',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              {isGenerating
                ? translate('Generating Script...')
                : translate('Generate Script')}
            </Button>
          </Box>
        </Tooltip>
      </ActionArea>
    </Container>
  );
}

export default PreGenerateScript;
